import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { nanoApi } from '@/services/learningManagementApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Edit, Trash2, Sparkles } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { Nano } from '@/types/learningManagement';

export const NanoDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAdminAuth();

  const canEdit = hasPermission('edit', 'nano');
  const canDelete = hasPermission('delete', 'nano');

  // Use entity hook for fetching nano
  const {
    data: nano,
    isLoading,
    error,
    refetch,
  } = useEntity<Nano>({
    fetchFn: (nanoId: string) => nanoApi.getById(nanoId),
    entityId: id,
    entityName: 'nano',
    enabled: !!id,
  });

  const isSystemDefined = nano?.nanoGenerated === 'SYSTEM_DEFINED';
  const canModify = canEdit && (!isSystemDefined || hasPermission('system_admin', 'nano'));

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Nano ID is required');
      await nanoApi.delete(id);
    },
    entityName: 'nano',
    operation: 'delete',
    onSuccess: () => {
      window.location.href = '/admin/nanos';
    },
  });

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this nano?')) return;
    await deleteMutation.mutate();
  };

  if (isLoading) {
    return <LoadingState message="Loading nano..." />;
  }

  if (error || !nano) {
    return <ErrorState error={error || new Error('Nano not found')} onRetry={refetch} />;
  }

  const nanoId = nano.id || '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/nanos">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              View Nano
            </h2>
            <p className="text-muted-foreground mt-1">Viewing: {nano.title}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canModify && (
            <Button asChild>
              <Link to={`/admin/nanos/${nanoId}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canDelete && !isSystemDefined && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="basic" className="space-y-6">
        <TabsList>
          <TabsTrigger value="basic">Basic Information</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Nano identification and classification</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <CopyableId id={nanoId} label="Nano ID" />
                <div className="space-y-2">
                  <Label>Title</Label>
                  <p className="mt-1">{nano.title}</p>
                </div>
              </div>

              {nano.description && (
                <div className="space-y-2">
                  <Label>Description</Label>
                  <textarea
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50"
                    value={nano.description}
                    disabled
                  />
                </div>
              )}

              {nano.content && (
                <div className="space-y-2">
                  <Label>Content</Label>
                  <textarea
                    className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50"
                    value={nano.content}
                    disabled
                  />
                </div>
              )}

              {nano.summary && (
                <div className="space-y-2">
                  <Label>Summary</Label>
                  <Input value={nano.summary} disabled />
                </div>
              )}

              {nano.skillId && (
                <CopyableId id={nano.skillId} label="Skill ID" />
              )}

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Input value={nano.category} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Tip Type *</Label>
                  <Input value={nano.tipType} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Difficulty Level</Label>
                  <Input value={nano.difficultyLevel} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Input value={nano.status} disabled />
                </div>
              </div>

              {nano.impactLevel && (
                <div className="space-y-2">
                  <Label>Impact Level</Label>
                  <Input value={nano.impactLevel} disabled />
                </div>
              )}

              {nano.implementationTimeMinutes && (
                <div className="space-y-2">
                  <Label>Implementation Time (Minutes)</Label>
                  <Input value={nano.implementationTimeMinutes.toString()} disabled />
                </div>
              )}

              {nano.targetAudience && (
                <div className="space-y-2">
                  <Label>Target Audience</Label>
                  <Input value={nano.targetAudience} disabled />
                </div>
              )}

              {nano.resources && nano.resources.length > 0 && (
                <div className="space-y-2">
                  <Label>Resources</Label>
                  <div className="flex flex-wrap gap-2">
                    {nano.resources.map((resource, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-sm"
                      >
                        {resource}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {nano.tags && nano.tags.length > 0 && (
                <div className="space-y-2">
                  <Label>Tags</Label>
                  <div className="flex flex-wrap gap-2">
                    {nano.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Generation Source</Label>
                <Input value={nano.nanoGenerated} disabled />
              </div>

              {nano.createdAt && (
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Created At</Label>
                    <Input value={new Date(nano.createdAt).toLocaleString()} disabled />
                  </div>
                  {nano.updatedAt && (
                    <div className="space-y-2">
                      <Label>Updated At</Label>
                      <Input value={new Date(nano.updatedAt).toLocaleString()} disabled />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NanoDetailPage;
